import { errorToast } from "@/utils/constants";
import React, { useRef, useEffect, useState } from "react";

export default function TimePickerDefault({
  value,
  onChange,
  className = "",
  placeholder,
  label = "",
  containerClassName = "",
  showAbove = false,
  required = false,
}: {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  label?: string;
  containerClassName?: string;
  showAbove?: boolean;
  required?: boolean;
}) {
  // Create refs to track input element and cursor position
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cursorPosition = useRef<number | null>(null);
  const hasFocus = useRef<boolean>(false);
  const [internalValue, setInternalValue] = useState(value);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Effect to restore cursor position and focus after render
  useEffect(() => {
    const input = inputRef.current;
    const position = cursorPosition.current;
    const shouldRestoreFocus = input && position !== null && hasFocus.current;

    if (shouldRestoreFocus) {
      // Restore cursor position
      input.selectionStart = position;
      input.selectionEnd = position;

      // Restore focus if this input should have focus
      if (document.activeElement !== input) {
        input.focus();
      }
    }
  }, [internalValue]); // Re-run when internalValue changes

  // Sync external value with internal state
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Function to check if the input is in valid 12-hour format (12:34 AM/PM)
  const isValid12HourFormat = (input: string): boolean => {
    // Regular expression for 12-hour format: hours:minutes AM/PM
    // Hours: 1-12, Minutes: 00-59, AM/PM: case insensitive
    const timeRegex = /^(1[0-2]|0?[1-9]):(0[0-9]|[1-5][0-9])\s*(am|pm|AM|PM)$/;
    return timeRegex.test(input);
  };

  // Function to format the input to 12-hour format if possible
  const formatTo12HourFormat = (input: string): string => {
    // Try to extract hours, minutes, and AM/PM
    const timeRegex =
      /^(1[0-2]|0?[1-9])(?::(0[0-9]|[1-5][0-9]))?\s*(am|pm|AM|PM)?$/i;
    const match = input.match(timeRegex);

    if (match) {
      const hours = match[1];
      const minutes = match[2] || "00";
      const period = match[3] ? match[3].toUpperCase() : "";

      if (period) {
        return `${hours}:${minutes} ${period}`;
      } else {
        return `${hours}:${minutes}`;
      }
    }

    return input;
  };

  // Function to commit the final value
  const commitValue = (finalValue: string) => {
    if (finalValue === "") {
      onChange(finalValue);
      return;
    }

    // Only commit if the value has changed and is in valid 12-hour format
    if (finalValue !== value) {
      // Try to format the input to 12-hour format
      const formattedValue = formatTo12HourFormat(finalValue);

      // Check if the formatted value is in valid 12-hour format
      if (isValid12HourFormat(formattedValue)) {
        onChange(formattedValue);
      } else {
        // If not valid, show error toast and keep the original value
        errorToast(
          "Invalid Time Format",
          "Please use 12-hour format (e.g., 12:34 AM/PM)"
        );
        setInternalValue(value);
      }
    }
  };

  return (
    <div className={`relative ${containerClassName}`}>
      {label && <label className="block mb-1">{label}</label>}
      <div
        className={showAbove ? "time-picker-above" : ""}
        data-show-above={showAbove}
      >
        <input
          type="time"
          value={value}
          onChange={(e) => {
            // console.log({ value: e.target.value });
            onChange(e.target.value);
          }}
          className={`border border-[#DADDE2] bg-[#FAFAFC] rounded-lg px-3 py-2 w-[122px] h-[35px] text-[14px] outline-none placeholder:text-[#A0AEC0] ${className}`}
        />
        {/* <input
          ref={inputRef}
          value={internalValue}
          className={`border border-[#DADDE2] bg-[#FAFAFC] rounded-lg px-3 py-2 w-[99px] h-[35px] outline-none ${className}`}
          placeholder={"00:00"}
          required={required}
          onChange={(e) => {
            // Store current cursor position before update
            cursorPosition.current = e.target.selectionStart;
            const newValue = e.target.value;
            setInternalValue(newValue);

            // // Clear any existing debounce timer
            // if (debounceTimerRef.current) {
            //   clearTimeout(debounceTimerRef.current);
            // }

            // // Set a new debounce timer to commit the value after typing stops
            // debounceTimerRef.current = setTimeout(() => {
            //   commitValue(newValue);
            // }, 3000);
          }}
          onFocus={() => {
            // Track that this input has focus
            hasFocus.current = true;
          }}
          onBlur={() => {
            // Track that this input lost focus
            hasFocus.current = false;

            // Clear any pending debounce timer
            // if (debounceTimerRef.current) {
            //   clearTimeout(debounceTimerRef.current);
            //   debounceTimerRef.current = null;
            // }

            // Commit the final value when input loses focus
            commitValue(internalValue);
          }}
        /> */}
      </div>
    </div>
  );
}
